import api from './client';
import { API_ENDPOINTS } from './endpoints';

export type ArAuditCycleStatus =
  | 'not_audited'
  | 'pending_associate_action'
  | 'resubmitted_to_qc'
  | 'rebuttal_submitted'
  | 'pending_supervisor_review'
  | 'closed';

export type ArAuditQueue = 'focus' | 'random';
export type ArAuditOutcome = 'no_error' | 'fatal' | 'non_fatal';

export interface ArAuditHistoryEntry {
  stage: 'qc_review' | 'associate_response' | 'qc_rebuttal_decision' | 'supervisor_review';
  action: string;
  by_id?: string;
  by_name?: string;
  at?: string;
  remarks?: string;
}

export interface ArAuditRecord {
  _id: string;
  accounts_receivable_id?: string;
  claim_id?: string;
  client_name?: string;
  patient_name?: string;
  patient_member_id?: string;
  dos?: string;
  payer_name?: string;
  charge_amount?: number;
  audit_queue?: string;
  auditor_name?: string;
  auditor_date?: string;
  audit_status?: string;
  error_classification?: string;
  error_type?: string;
  error_description?: string;
  rebuttal_comment?: string;
  qa_response?: string;
  audit_cycle_status?: ArAuditCycleStatus;
  audit_needs_attention?: boolean;
  audit_associate_id?: string;
  audit_associate_name?: string;
  audit_history?: ArAuditHistoryEntry[];
  updatedAt?: string;
}

export interface ArAuditListResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: {
    records?: ArAuditRecord[];
    count?: number;
  };
}

export interface ArAuditMutationResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: {
    record?: ArAuditRecord;
  };
}

export interface GetArAuditQueueParams {
  client_id?: string;
  audit_cycle_status?: string;
  page?: number;
  limit?: number;
  [key: string]: string | number | boolean | undefined;
}

export const getArAuditQueue = async (params?: GetArAuditQueueParams): Promise<ArAuditListResponse> => {
  return api.get<ArAuditListResponse>(API_ENDPOINTS.arAudit.queue, { params });
};

export const getMyArAuditAlerts = async (): Promise<ArAuditListResponse> => {
  return api.get<ArAuditListResponse>(API_ENDPOINTS.arAudit.myAlerts);
};

export interface SubmitArAuditReviewPayload {
  audit_queue: ArAuditQueue;
  outcome: ArAuditOutcome;
  error_classification?: string;
  error_type?: string;
  error_description?: string;
  remarks?: string;
}

export const submitArAuditReview = async (
  id: string,
  payload: SubmitArAuditReviewPayload,
): Promise<ArAuditMutationResponse> => {
  return api.post<ArAuditMutationResponse, SubmitArAuditReviewPayload>(
    `${API_ENDPOINTS.arAudit.base}/${id}/review`,
    payload,
  );
};

export interface SubmitArAuditAssociateResponsePayload {
  response_type: 'rework' | 'rebuttal';
  remarks?: string;
}

export const submitArAuditAssociateResponse = async (
  id: string,
  payload: SubmitArAuditAssociateResponsePayload,
): Promise<ArAuditMutationResponse> => {
  return api.post<ArAuditMutationResponse, SubmitArAuditAssociateResponsePayload>(
    `${API_ENDPOINTS.arAudit.base}/${id}/respond`,
    payload,
  );
};

export interface DecideArAuditRebuttalPayload {
  decision: 'accept' | 'reject';
  remarks?: string;
}

export const decideArAuditRebuttal = async (
  id: string,
  payload: DecideArAuditRebuttalPayload,
): Promise<ArAuditMutationResponse> => {
  return api.post<ArAuditMutationResponse, DecideArAuditRebuttalPayload>(
    `${API_ENDPOINTS.arAudit.base}/${id}/rebuttal-decision`,
    payload,
  );
};

export interface DecideArAuditSupervisorReviewPayload {
  decision: 'close' | 'send_back';
  remarks?: string;
}

export const decideArAuditSupervisorReview = async (
  id: string,
  payload: DecideArAuditSupervisorReviewPayload,
): Promise<ArAuditMutationResponse> => {
  return api.post<ArAuditMutationResponse, DecideArAuditSupervisorReviewPayload>(
    `${API_ENDPOINTS.arAudit.base}/${id}/supervisor-decision`,
    payload,
  );
};
